import { createStart, createMiddleware } from "@tanstack/react-start";
import { getRequestHost, setResponseHeader } from "@tanstack/react-start/server";

import { renderErrorPage } from "./lib/error-page";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * Derive the Supabase HTTP + WebSocket origins so the CSP `connect-src` allows
 * the app's own backend (Data API, Auth, Realtime) without opening up to `https:`.
 */
function supabaseConnectOrigins(): string[] {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!url) return [];
  try {
    const { host } = new URL(url);
    return [`https://${host}`, `wss://${host}`];
  } catch {
    return [];
  }
}

/**
 * Content-Security-Policy.
 *
 * - `object-src 'none'` + `base-uri 'self'` close the classic XSS injection
 *   vectors that a bare `default-src` misses.
 * - `require-trusted-types-for 'script'` mitigates DOM-based XSS by forcing all
 *   dangerous sink writes (innerHTML, script.src, …) through a Trusted Types
 *   policy. The app registers a `default` policy in the document head so the
 *   existing GTM loader / dangerouslySetInnerHTML keep working.
 * - `'unsafe-inline'` on script/style is required because the app ships inline
 *   bootstrap scripts (GTM, font swap) and inline styles; combined with the
 *   Trusted Types requirement this still blocks injected `<script src>` /
 *   sink-based payloads.
 * - In dev we additionally allow `'unsafe-eval'` and `ws:`/`wss:` so Vite HMR
 *   keeps working; production stays strict.
 */
function buildCsp({ dev, framable }: { dev: boolean; framable: boolean }): string {
  const supabase = supabaseConnectOrigins();

  const scriptSrc = [
    "'self'",
    "'unsafe-inline'",
    "https://www.googletagmanager.com",
    "https://www.google-analytics.com",
    ...(dev ? ["'unsafe-eval'"] : []),
  ];

  const connectSrc = [
    "'self'",
    ...supabase,
    "https://dgr2n1ui.api.sanity.io",
    "wss://dgr2n1ui.api.sanity.io",
    "https://*.api.sanity.io",
    "wss://*.api.sanity.io",
    "https://*.apicdn.sanity.io",
    "https://www.google-analytics.com",
    "https://region1.google-analytics.com",
    "https://www.googletagmanager.com",
    "https://stats.g.doubleclick.net",
    ...(dev ? ["ws:", "wss:"] : []),
  ];

  // Allow the Lovable editor / preview to frame the app; deny everyone else.
  const frameAncestors = framable
    ? "frame-ancestors 'self' https://*.lovable.app https://*.lovable.dev http://localhost:*"
    : "frame-ancestors 'none'";

  return [
    "default-src 'self'",
    "base-uri 'self'",
    "object-src 'none'",
    "form-action 'self'",
    frameAncestors,
    "img-src 'self' data: blob: https:",
    "font-src 'self' https://fonts.gstatic.com data:",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    `script-src ${scriptSrc.join(" ")}`,
    `connect-src ${connectSrc.join(" ")}`,
    "frame-src 'self' https://leadflow.puulse.ai https://www.google.com",
    "worker-src 'self' blob:",
    "manifest-src 'self'",
    "upgrade-insecure-requests",
    "require-trusted-types-for 'script'",
    "trusted-types default 'allow-duplicates'",
  ].join("; ");
}

const securityHeadersMiddleware = createMiddleware().server(async ({ next }) => {
  const dev = import.meta.env.DEV;
  const host = (getRequestHost() ?? "").toLowerCase();
  // The Lovable editor renders the preview inside an iframe, so preview/dev
  // hosts must remain framable. Real deployments (custom domain / Vercel) do not.
  const framable =
    host.includes("lovable") ||
    host.includes("localhost") ||
    host.startsWith("127.");

  setResponseHeader("Content-Security-Policy", buildCsp({ dev, framable }));
  setResponseHeader(
    "Strict-Transport-Security",
    "max-age=63072000; includeSubDomains; preload",
  );
  setResponseHeader("Cross-Origin-Opener-Policy", "same-origin");
  setResponseHeader("X-Content-Type-Options", "nosniff");
  setResponseHeader("Referrer-Policy", "strict-origin-when-cross-origin");
  setResponseHeader(
    "Permissions-Policy",
    "camera=(), microphone=(), geolocation=(), browsing-topics=()",
  );
  // X-Frame-Options has no allowlist form, so only send the hard DENY where the
  // app is never meant to be framed (i.e. real deployments). frame-ancestors
  // above is the modern equivalent and covers the preview case.
  if (!framable) {
    setResponseHeader("X-Frame-Options", "DENY");
  }

  return next();
});

export const startInstance = createStart(() => ({
  requestMiddleware: [securityHeadersMiddleware, errorMiddleware],
}));
